import type { Availability } from "../../common/jsonRpcTypes";

/**
 * Rules for interpreting the availability outcome of a `/createBooking` check.
 *
 * These mirror DMN's legacy `/book` page so the widget reaches the same
 * conclusions as the journey it replaces. DMN renders the whole journey on one
 * screen and gates the customer-details block on the availability action, so
 * the equivalent decisions here are taken when the guest moves to the details
 * stage.
 */

/**
 * Whether the booking will be submitted as an enquiry rather than a booking.
 *
 * Mirrors DMN's submit-button label in `templates/book/stage-initial.html`
 * (`availability.action === 'accept' ? 'book_now' : 'enquire_now'`) and the
 * booking summary sentence in `js/controllers/bookController.js`
 * (`booking.do_enquiry || action !== 'accept'`). The `do_enquiry` half of that
 * rule is held separately, as `isEnquiry` on the wizard state, and set when the
 * guest explicitly chooses to continue as an enquiry.
 *
 * A missing availability result is treated as a booking. DMN defaults it to
 * `reject`, but every stage past the booking-details form is reached through an
 * availability check, so this only covers states the journey cannot produce.
 */
export function isEnquiryOutcome(availability: Availability | null | undefined): boolean {
  return !!availability && availability.action !== "accept";
}

/**
 * Whether the guest has to resolve the availability outcome themselves before
 * the journey can continue to the customer-details stage.
 *
 * Mirrors the guard DMN repeats around its customer-details block, rewards
 * banner and offers list in `templates/book/stage-initial.html`:
 * `(availability.action != 'suggest_times' || booking.do_enquiry) && availability.action != 'reject'`
 *
 * - `reject` — the slot cannot be booked, so the details have to change.
 * - `suggest_times` — alternative times and venues are on offer; the guest
 *   either picks one or explicitly continues as an enquiry.
 *
 * The `do_enquiry` exemption is not repeated here: choosing to continue as an
 * enquiry navigates the guest onwards directly, so this guard is never consulted
 * for that decision.
 */
export function blocksProgressToDetails(availability: Availability | null | undefined): boolean {
  const action = availability?.action;
  return action === "reject" || action === "suggest_times";
}
